import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { FitConfig } from '../models/fit.model';
import * as utils from '../../shared/helpers/utils';

@Injectable({
  providedIn: 'root'
})
export class Workaround {

  private round(value: any, precision: any) {
    var multiplier = Math.pow(10, precision || 0);
    return Math.round(value * multiplier) / multiplier;
  }

  private avg(value1: any, value2: any) {
    return (value1 + value2) / 2.0;
  }

  private reduce_assimetry_naso(right_val: any, left_val: any) {
    let M = this.avg(right_val, left_val);
    const A = 0.3;
    const D = (A * (M - right_val));
    right_val = this.round(M - D, 1);
    left_val = this.round(M + D, 1);
    return [right_val, left_val]
  }

  private reduce_assimetry_height(right_val: any, left_val: any) {
    let M = this.avg(right_val, left_val);
    const A = 0.5;
    const D = (A * (M - right_val));
    right_val = this.round(M - D, 1);
    left_val = this.round(M + D, 1);
    return [right_val, left_val]
  }

  private back_vertex_distance(fits: any, workaround_version: any) {
    let M = this.avg(fits.measures.BVD_R, fits.measures.BVD_L);
    const A = 0.3;
    const D = (A * (M - fits.measures.BVD_R));
    fits.measures.BVD_R = this.round(M - D - 4.0, 1);
    fits.measures.BVD_L = this.round(M + D - 4.0, 1);
    if (workaround_version >= 2) {
      fits.measures.BVD_R = this.round(fits.measures.BVD_R - ((fits.measures.BVD_R - 12) * 0.3), 1)
      fits.measures.BVD_L = this.round(fits.measures.BVD_L - ((fits.measures.BVD_L - 12) * 0.3), 1);
    }
    return fits;
  }

  private interpupillary_distance(fits: any, workaround_version: any) {
    if ("IPD_noframe" in fits.measures && workaround_version >= 4) {
      let oldNaso = fits.measures.Naso_R + fits.measures.Naso_L
      let correctionParam = fits.measures['IPD_noframe'] / oldNaso
      fits.measures.Naso_R = fits.measures.Naso_R * correctionParam
      fits.measures.Naso_L = fits.measures.Naso_L * correctionParam
    }

    let nasos = this.reduce_assimetry_naso(fits.measures.Naso_R, fits.measures.Naso_L)
    fits.measures.Naso_R = nasos[0];
    fits.measures.Naso_L = nasos[1];
    return fits;
  }

  public apply_workaround(fit: any) {
    let workaround_version = 1
    let heightFrame;
    if ("workaround_version" in fit) {
      workaround_version = fit["workaround_version"]
    }
    fit = this.back_vertex_distance(fit, workaround_version);
    fit = this.interpupillary_distance(fit, workaround_version);
    if ("HeightFrame_R" in fit.measures) {
      fit.measures.HeightFrame_R = (fit.measures.HeightFrame_R > fit.measures.HeightBOX_R) ?
        fit.measures.HeightBOX_R : fit.measures.HeightFrame_R;

      fit.measures.HeightFrame_L = (fit.measures.HeightFrame_L > fit.measures.HeightBOX_L) ?
        fit.measures.HeightBOX_L : fit.measures.HeightFrame_L;

      if (workaround_version == 2) {
        heightFrame = this.reduce_assimetry_naso(fit.measures.HeightFrame_R, fit.measures.HeightFrame_L)
        fit.measures.HeightFrame_R = heightFrame[0]
        fit.measures.HeightFrame_L = heightFrame[1]
      } else if (workaround_version >= 3) {
        heightFrame = this.reduce_assimetry_height(fit.measures.HeightFrame_R, fit.measures.HeightFrame_L)
        fit.measures.HeightFrame_R = heightFrame[0]
        fit.measures.HeightFrame_L = heightFrame[1]
      }
    }
    if (workaround_version == 2) {
      heightFrame = this.reduce_assimetry_naso(fit.measures.HeightBOX_R, fit.measures.HeightBOX_L)
      fit.measures.HeightBOX_R = heightFrame[0]
      fit.measures.HeightBOX_L = heightFrame[1]
    } else if (workaround_version >= 3) {
      heightFrame = this.reduce_assimetry_height(fit.measures.HeightBOX_R, fit.measures.HeightBOX_L)
      fit.measures.HeightBOX_R = heightFrame[0]
      fit.measures.HeightBOX_L = heightFrame[1]
    }
    return fit;
  }
  
}

@Injectable({
  providedIn: 'root'
})
export class FitService {

  FitConfig: FitConfig = {};

  auth = utils?.localStorageGetItem('auth');

  public currentFitConfigSubject: BehaviorSubject<FitConfig> = new BehaviorSubject(this.FitConfig);
  public currentFitConfig!: Observable<FitConfig>;

  constructor(
    private httpClient: HttpClient,
    private workAround: Workaround
  ) { }

  index(entities: string): Observable<any> {
    return this.httpClient.post(environment.apiMadeForYouUrl + '/backoffice/tags/list', { shop_id: entities });
  }

  index2(queryParams: string): Observable<any> {
    //return this.httpClient.get(environment.apiFitUrl + '/fit/list?entity_id=admin&role=admin&per_page=2&reverse_search=false&order=desc&sort=date&shop_id=CR-SURFACING');
    return this.httpClient.get(environment.apiFitUrl + '/fit/list?' + queryParams);

  }

  indexGet(entities: string, queryParams: string): Observable<any> {
    return this.httpClient.get(environment.apiMadeForYouUrl + '/backoffice/tags/list?shop_id=' + entities + '&' + queryParams);
  }

  indexData(_tagId: string): Observable<any> {
    return this.httpClient.post(environment.apiFitUrl + '/fit/data', { tag_id: _tagId });
  }

  errors(entities: string): Observable<any> {
    return this.httpClient.post(environment.apiFitUrl + '/fit/list_error', { shop_id: entities });
  }

  updateReference(tagId: string, reference: string): Observable<any> {
    return this.httpClient.post(environment.apiFitUrl + '/fit/update', { tag_id: tagId, internal_reference: reference });
  }

  updateComments(tagId: string, comments: string): Observable<any> {
    return this.httpClient.post(environment.apiFitUrl + '/fit/update', { tag_id: tagId, comments: comments });
  }

  tar(tagId: string): Observable<any> {
    return this.httpClient.get(environment.apiFitUrl + '/fit/create_tar/' + tagId + '?redirect=false');
  }

  openInvision(tagId: string, show_pupils = false) {
    //window.open(environment.invisionUrl + '?fitid=' + tagId, '_blank');
    let options_check_folder = new HttpHeaders();
    return new Promise((resolve, reject) => {
      this.httpClient.get(environment.apiFitUrl + `/fit/check_folder/${tagId}`, { headers: options_check_folder })
        .subscribe(
          () => {
            let headers: HttpHeaders = new HttpHeaders;
            headers.set('Content-Type', 'application/json');
            headers.set('Access-Control-Allow-Credentials', 'true');
            this.httpClient.get(
              environment.invisionUrl_api.concat("/login"),
              { headers: headers, withCredentials: true }
            ).subscribe(
              () => {
                window.open(environment.invisionUrl.concat("?fitid=").concat(tagId.toString()).concat(`${show_pupils ? "&pupils=1" : ""}`), "_blank")!.focus();
                resolve("done")
              },
              (error) => {
                console.log("Error on fit login")
                console.log(error)
              }
            )
          },
          err => {
            reject(err);
          });
    })
  }

  open360Depict(tagId?: string) {
    //window.open(environment.depictUrl + '?fitid=' + tagId, '_blank');

    return new Promise(resolve => {
      let headers: HttpHeaders = new HttpHeaders;
      headers.set('Content-Type', 'application/json');
      headers.set('Access-Control-Allow-Credentials', 'true');
      this.httpClient.get(
        environment.depictUrlThickness_api.concat("/login"),
        { headers: headers, withCredentials: true }
      ).subscribe(
        () => {
          let url: string = tagId ? environment.depictUrl.concat("?fitid=").concat(tagId) : environment.depictUrl;
          console.log(url);
          window.open(url, "_blank")!.focus();
          resolve("done")
        },
        (error) => {
          console.log("Error on Thickness login")
          console.log(error)
        }
      )
    });
  }

  open360DepictFrozen(frozenTech: string) {
    return new Promise(resolve => {
      let headers: HttpHeaders = new HttpHeaders;
      headers.set('Content-Type', 'application/json');
      headers.set('Access-Control-Allow-Credentials', 'true');
      this.httpClient.get(
        environment.depictUrlThickness_api.concat("/login"),
        { headers: headers, withCredentials: true }
      ).subscribe(
        () => {
          const url = environment.depictUrl.concat("?frozen=").concat(frozenTech);
          console.log(url);
          window.open(url, "_blank")!.focus();
          resolve("done")
        },
        (error) => {
          console.log("Error on Thickness login")
          console.log(error)
        }
      )
    });
  }

  delete(testId: string, recover: boolean, hardDelete: boolean = false, mail: string | null = null): Observable<any> {
    return this.httpClient.delete(environment.apiFitUrl + '/fit/delete/' + testId, { body: {"recover": recover, "hardDelete": hardDelete, "recipientEmail": mail} });
  }

  /*
public goToThickness(tag_id: string): Promise<String> {

  let url_fitid = environment.api.measures.concat(tag_id.toString())-

  return new Promise(resolve => {
    this.withInfo((options) => {
      options = options.set('Content-Type', 'application/json');
      this._http.get(
        url_fitid,
        { headers: options }
      ).subscribe(
        () => {
          options = options.set('Access-Control-Allow-Credentials', 'true');
          this._http.get(
            environment.api.thickness_api.concat("login"),
            { headers: options, withCredentials: true }
          ).subscribe(
            () => {
              window.open(environment.api.thickness.concat("?fitid=").concat(tag_id.toString()), "_blank").focus();
              resolve("done")
            },
            (error) => {
              console.log("Error on Thickness login")
              console.log(error)
            }
          )
        },
        (error) => {
          console.log("Error on Thickness login")
          console.log(error)
        }
      );
    })
  });
}
*/

  public get currentFitConfigValue(): FitConfig {
    return this.currentFitConfigSubject.value;
  }

  public setCurrentFitConfig(_value: FitConfig | null) {

    this.currentFitConfigSubject.next(this.FitConfig)
  }
  public setCurrentFitParamConfig(_param: string, _value: any) {
    this.currentFitConfigSubject.value[_param] = _value;
    this.currentFitConfigSubject.next(this.currentFitConfigSubject.value);
  }

  public apply_workaround(fit: any): void {
    this.workAround.apply_workaround(fit);
  }

}
